import { ReactNode, Ref, forwardRef, useEffect } from 'react';
import PopupJs from 'reactjs-popup';
import { PopupActions, PopupProps } from 'reactjs-popup/dist/types';
import { useTheme } from 'styled-components/macro';

import { CloseButtonIconStyled, getContentStyles, getOverlayStyles } from './Popup.styled';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import useScrollLock from 'hooks/common/useScrollLock';
import { CloseIcon } from 'icons';

type PopupPropsT = PopupProps & {
  children: ReactNode;
  popupId?: ScrollLockIdE;
  isOpen?: boolean;
  showCloseBtn?: boolean;
  ref?: Ref<PopupActions>;
};

const Popup = forwardRef<PopupActions, PopupPropsT>(
  (
    {
      children,
      onOpen,
      onClose,
      popupId,
      isOpen = false,
      showCloseBtn = false,
      contentStyle,
      ...restProps
    },
    ref
  ) => {
    /**
     * Hooks
     */
    const theme = useTheme();
    const { isMobile } = useResponsive();
    const { enableScroll, disableScroll } = useScrollLock();

    /**
     * Handlers
     */
    const handleOpen = () => {
      if (onOpen) {
        onOpen();
      }
    };
    const handleClose = () => {
      if (onClose) {
        onClose();
      }

      if (popupId) {
        enableScroll(popupId);
      }
    };

    useEffect(() => {
      if (isOpen && popupId) {
        disableScroll(popupId);
      }
    }, [isOpen, popupId, disableScroll]);

    return (
      <PopupJs
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        overlayStyle={getOverlayStyles(theme)}
        contentStyle={getContentStyles(theme, isMobile, contentStyle)}
        ref={ref}
        {...restProps}
      >
        {showCloseBtn && (
          <CloseButtonIconStyled
            variant="transparent"
            size={isMobile ? 'md' : 'sm'}
            icon={<CloseIcon />}
            onClick={handleClose}
          />
        )}
        {children}
      </PopupJs>
    );
  }
);

export default Popup;
